import { Component, ErrorInfo, ReactNode } from "react";
import { Flex } from "styled-system/jsx";
import { Text } from "@ttc3k/trekker";

type TripperErrorBoundaryProps = {
  children: ReactNode;
};

type TripperErrorBoundaryState = {
  hasError: boolean;
  errorMessage: string | null;
  retryCount: number;
};

/** Transient errors that should be retried silently rather than shown to users. */
const TRANSIENT_PATTERNS = [
  "Style is not done loading",
];

function isTransient(message: string | null): boolean {
  if (!message) return false;
  return TRANSIENT_PATTERNS.some((p) => message.includes(p));
}

const MAX_RETRIES = 3;

export class TripperErrorBoundary extends Component<
  TripperErrorBoundaryProps,
  TripperErrorBoundaryState
> {
  state: TripperErrorBoundaryState = { hasError: false, errorMessage: null, retryCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<TripperErrorBoundaryState> {
    return { hasError: true, errorMessage: `${error?.name}: ${error?.message}` };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[TripperWidget] Render error:", error, errorInfo);
  }

  componentDidUpdate(_: TripperErrorBoundaryProps, prevState: TripperErrorBoundaryState): void {
    if (
      this.state.hasError &&
      !prevState.hasError &&
      isTransient(this.state.errorMessage) &&
      this.state.retryCount < MAX_RETRIES
    ) {
      // Auto-retry transient errors after a short delay
      setTimeout(() => {
        this.setState((s) => ({
          hasError: false,
          errorMessage: null,
          retryCount: s.retryCount + 1,
        }));
      }, 500);
    }
  }

  render() {
    if (this.state.hasError) {
      // Transient errors that will auto-retry: render nothing (map will re-mount)
      if (isTransient(this.state.errorMessage) && this.state.retryCount < MAX_RETRIES) {
        return null;
      }

      return (
        <Flex h={"100%"} alignItems={"center"} justifyContent={"center"} flexDirection={"column"} gap={"8px"} p={"16px"}>
          <Text visual={"body"}>Unable to render itinerary</Text>
          {this.state.errorMessage && (
            <pre style={{ fontSize: 11, color: "#b00", whiteSpace: "pre-wrap", wordBreak: "break-all", maxWidth: "100%", textAlign: "center" }}>
              {this.state.errorMessage}
            </pre>
          )}
        </Flex>
      );
    }

    return this.props.children;
  }
}
